import type {
  ChannelSummary,
  CreateChannelRequest,
  ManageChannelResponse,
  UpdateChannelRequest,
} from '@pulsify/shared'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { organizerApi } from '~/entities/channel/api/organizer'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

const keyFor = (businessId: string | null) => queryKey(QueryKey.ORGANIZER_PROFILE, businessId)

function mergeSaved(queryClient: QueryClient, businessId: string | null, saved: ChannelSummary) {
  queryClient.setQueriesData<ManageChannelResponse['channel']>(
    { queryKey: keyFor(businessId) },
    (current) => (current?.id === saved.id ? { ...current, ...saved } : current),
  )
}

/** One organizer page of the business: the given one, or the oldest when none is given. */
export function useOrganizerProfile(
  businessId: string | null,
  channelId?: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKey(QueryKey.ORGANIZER_PROFILE, businessId, channelId ?? null),
    queryFn: () => organizerApi.get(businessId ?? '', channelId),
    enabled: !!businessId && (options.enabled ?? true),
  })
}

export function useSaveOrganizerProfile(businessId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      input: { create: CreateChannelRequest } | { id: string; patch: UpdateChannelRequest },
    ) =>
      'create' in input
        ? organizerApi.create(input.create)
        : organizerApi.update(input.id, input.patch),
    onSuccess: (saved) => {
      mergeSaved(queryClient, businessId, saved)
      queryClient.invalidateQueries({ queryKey: keyFor(businessId) })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.ORGANIZATION_PROFILES, businessId),
      })
    },
  })
}

export function useSetOrganizerPublished(businessId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; published: boolean }) =>
      organizerApi.setPublished(input.id, input.published),
    onSuccess: (saved) => {
      mergeSaved(queryClient, businessId, saved)
      queryClient.invalidateQueries({ queryKey: keyFor(businessId) })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.ORGANIZATION_PROFILES, businessId),
      })
    },
  })
}

export function useUploadOrganizerPicture(businessId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; slot: 'avatar' | 'cover'; file: File }) =>
      organizerApi.uploadPicture(input.id, input.slot, input.file),
    onSuccess: (saved) => {
      mergeSaved(queryClient, businessId, saved)
      return queryClient.invalidateQueries({ queryKey: keyFor(businessId) })
    },
  })
}
